import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import Subtitle from "../common/Subtitle";
import { CgDetailsMore } from "react-icons/cg";
import { CiBoxList } from "react-icons/ci";
import ReactFlow from "reactflow";
import "reactflow/dist/style.css";
import { GeneralNode, getNodes, getEdges } from "./RunbookDetails.viewModel";
import { runbookStepActions } from "../../actions/runbookStep.actions";
import Utils from "../../utils";
import { Badge, Button, Modal, Placeholder, Spinner } from "react-bootstrap";
import useRunbookTitle from "../../hooks/runbookTitle";
import DetailsModal from "../common/DetailsModal";
import { TbStepInto } from "react-icons/tb";
import ConfirmModal from "../common/ConfirmModal";
import RunbookDetailsEmpty from "./RunbookDetailsEmpty";
import { runbookStepActionsConstants } from "../../constants/runbookStepActions.constants";
import StepUpdateFormModal from "./StepUpdateFormModal";
import { runbookActions } from "../../actions/runbook.actions";
import { MdGppBad, MdGppGood } from "react-icons/md";

function RunbookDetails({ user }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const runbookStepReducer = useSelector((state) => state.runbookStep);
  const runbookReducer = useSelector((state) => state.runbook);

  const {
    runbookTitle,
    runbookTitleLoading,
    runbookGuid,
    clientId,
    clientGuid,
  } = useRunbookTitle();
  const [viewDetailsModalStepId, setViewDetailsModalStepId] = useState("");
  const [confirmValidateModalShow, setConfirmValidateModalShow] =
    useState(false);
  const [validateResultModalShow, setValidateResultModalShow] = useState(false);

  const [actionStepId, setActionStepId] = useState("");
  const [actionStep, setActionStep] = useState("");

  const nodeTypes = {
    generalNode: GeneralNode,
  };

  useEffect(() => {
    const runbookId = Utils.getQueryStringParams(location)["runbookId"];

    dispatch(runbookStepActions.get({ runbookId }));

    if (!runbookStepReducer.actions)
      dispatch(runbookStepActions.getAllActions());

    // eslint-disable-next-line
  }, []);

  const handleStepClick = (stepId) => {
    setViewDetailsModalStepId(stepId);
  };

  const handleUpdate = (stepId) => {
    setActionStep(runbookStepActionsConstants.UPDATE);
    setActionStepId(stepId);
  };

  const handleDelete = (stepId) => {
    setActionStep(runbookStepActionsConstants.DELETE);
    setActionStepId(stepId);
  };

  const handleAddForLoopEndLoop = (stepId) => {
    setActionStep(runbookStepActionsConstants.ADD_FOR_LOOP_END_LOOP);
    setActionStepId(stepId);
  };

  const handleAddAfter = (stepId) => {
    setActionStep(runbookStepActionsConstants.ADD_AFTER);
    setActionStepId(stepId);
  };

  const handleAddBefore = (stepId) => {
    setActionStep(runbookStepActionsConstants.ADD_BEFORE);
    setActionStepId(stepId);
  };

  const handleAddTrue = (stepId) => {
    setActionStep(runbookStepActionsConstants.ADD_TRUE);
    setActionStepId(stepId);
  };

  const handleAddFalse = (stepId) => {
    setActionStep(runbookStepActionsConstants.ADD_FALSE);
    setActionStepId(stepId);
  };

  const handleConfirmAction = () => {
    let requestParameters = {
      stepId: actionStepId,
      runbookVersion: 1,
      runbookId: Utils.getQueryStringParams(location)["runbookId"],
      runbookGuid: runbookGuid,
    };

    switch (actionStep) {
      case runbookStepActionsConstants.ADD_AFTER:
        dispatch(runbookStepActions.addAfter(requestParameters));
        break;
      case runbookStepActionsConstants.ADD_BEFORE:
        dispatch(runbookStepActions.addBefore(requestParameters));
        break;
      case runbookStepActionsConstants.ADD_FOR_LOOP_END_LOOP:
        dispatch(runbookStepActions.addForLoopEndLoop(requestParameters));
        break;
      case runbookStepActionsConstants.ADD_TRUE:
        dispatch(runbookStepActions.addTrue(requestParameters));
        break;
      case runbookStepActionsConstants.ADD_FALSE:
        dispatch(runbookStepActions.addFalse(requestParameters));
        break;
      case runbookStepActionsConstants.DELETE:
        dispatch(runbookStepActions.remove(requestParameters));
        break;
      default:
        break;
    }

    setActionStepId("");
    setActionStep("");
  };

  const handleModalClose = () => {
    setViewDetailsModalStepId("");
  };

  const handleValidateRunbook = () => {
    setConfirmValidateModalShow(false);
    setValidateResultModalShow(true);

    const requestBody = {
      runbookId: Utils.getQueryStringParams(location)["runbookId"],
      clientId: clientId,
      clientGuid: clientGuid,
      runbookGuid: runbookGuid,
    };

    dispatch(runbookActions.validate(requestBody));
  };

  const initialNodes = runbookStepReducer.response
    ? getNodes(
        runbookStepReducer.response,
        handleStepClick,
        handleUpdate,
        handleDelete,
        handleAddForLoopEndLoop,
        handleAddAfter,
        handleAddBefore,
        handleAddTrue,
        handleAddFalse
      )
    : [];

  const initialEdges = runbookStepReducer.response
    ? getEdges(runbookStepReducer.response)
    : [];

  const loading = runbookTitleLoading || runbookStepReducer.loading;

  const formatStepData = (stepData) => {
    if (!stepData) return {};

    return {
      ...stepData,
      action: <Badge bg="primary">{stepData.action}</Badge>,
      connector: stepData.connector
        ? `${stepData.connector.connectorNumber} (${
            stepData.connector.system
          } - ${stepData.connector.loginEndpoint}${
            stepData.connector.companyId
              ? ` - ${stepData.connector.companyId}`
              : ""
          })`
        : null,
    };
  };

  return (
    <>
      <Title text="Runbook Details" icon={<CgDetailsMore className="mb-1" />} />
      {!loading && (
        <Subtitle text={runbookTitle} icon={<CiBoxList className="mb-1" />} />
      )}

      {loading && (
        <Placeholder as={"div"} style={{ height: "100%" }} animation="glow">
          <Placeholder xs={12} size="lg" style={{ height: "100%" }} />
        </Placeholder>
      )}

      {!loading && (
        <>
          <div className="d-grid gap-2 mb-3">
            <Button
              variant="outline-primary"
              size="lg"
              onClick={() => setConfirmValidateModalShow(true)}
            >
              <MdGppGood className="mb-1 me-2" />
              Validate Runbook
            </Button>
          </div>
          {initialNodes.length > 0 ? (
            <>
              <div style={{ height: "85%" }} className="mt-4">
                <ReactFlow
                  nodes={initialNodes}
                  edges={initialEdges}
                  proOptions={{ hideAttribution: true }}
                  nodeTypes={nodeTypes}
                  zoomOnScroll={false}
                  zoomOnDoubleClick={false}
                  panOnDrag={false}
                ></ReactFlow>
              </div>
              <DetailsModal
                show={viewDetailsModalStepId !== ""}
                title={
                  viewDetailsModalStepId !== "" && (
                    <>
                      <TbStepInto className="mb-1 me-2" />
                      {
                        initialNodes.filter(
                          (s) => s.id === viewDetailsModalStepId
                        )[0].data.step.stepNumber
                      }{" "}
                      Details{" "}
                      {initialNodes.filter(
                        (s) => s.id === viewDetailsModalStepId
                      )[0].data.step.system && (
                        <>
                          {" "}
                          -{" "}
                          <small>
                            {
                              initialNodes.filter(
                                (s) => s.id === viewDetailsModalStepId
                              )[0].data.step.system
                            }
                          </small>
                        </>
                      )}
                    </>
                  )
                }
                onClose={handleModalClose}
                data={
                  viewDetailsModalStepId !== ""
                    ? formatStepData(
                        initialNodes.filter(
                          (s) => s.id === viewDetailsModalStepId
                        )[0].data.step
                      )
                    : {}
                }
              />

              <ConfirmModal
                show={
                  !!actionStep &&
                  actionStep !== runbookStepActionsConstants.UPDATE
                }
                title={`${actionStep} Step`}
                question={`Are you sure you want to ${actionStep} step ${actionStepId}?`}
                noButtonText="No"
                yesButtonText="Yes, please"
                handleClose={() => {
                  setActionStep("");
                  setActionStepId("");
                }}
                handleConfirm={() => {
                  handleConfirmAction();
                }}
              />

              <StepUpdateFormModal
                show={actionStep === runbookStepActionsConstants.UPDATE}
                onClose={() => {
                  setActionStep("");
                  setActionStepId("");
                }}
                stepId={actionStepId}
                user={user}
                runbookId={Utils.getQueryStringParams(location)["runbookId"]}
                runbookGuid={runbookGuid}
              />
            </>
          ) : (
            <RunbookDetailsEmpty />
          )}
        </>
      )}

      <ConfirmModal
        show={confirmValidateModalShow}
        title={`Validate Runbook - ${
          Utils.getQueryStringParams(location)["runbookId"]
        }`}
        question={`Are you sure you want to validate runbook ${
          Utils.getQueryStringParams(location)["runbookId"]
        }?`}
        noButtonText="No"
        yesButtonText="Yes, please"
        handleClose={() => setConfirmValidateModalShow(false)}
        handleConfirm={handleValidateRunbook}
      />

      <Modal
        show={validateResultModalShow}
        onHide={() => setValidateResultModalShow(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Validate Runbook -{" "}
            {Utils.getQueryStringParams(location)["runbookId"]}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {runbookReducer.loadingValidate && (
            <>
              Validating...
              <Spinner className="float-end" size="sm" variant="primary" />
              <div className="clearfix" />
            </>
          )}

          {!runbookReducer.loadingValidate && (
            <>
              {runbookReducer.validateResponse &&
                !runbookReducer.validateResponse.hasErrors && (
                  <div className="text-success">
                    <MdGppGood className="me-2 mb-1" />
                    Validation successful!
                  </div>
                )}
              {runbookReducer.validateResponse?.hasErrors && (
                <div className="text-danger">
                  <MdGppBad className="me-2 mb-1" />
                  {runbookReducer.validateResponse.message ||
                    "Validation failed. Please check the details."}
                </div>
              )}
              {runbookReducer.validateError && (
                <div className="text-danger">
                  <MdGppBad className="me-2 mb-1" />
                  {runbookReducer.validateError}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setValidateResultModalShow(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RunbookDetails;
